import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress
} from '@mui/material';
import { SystemSettingsService } from '../../services/systemSettingsService';
import { useAuth } from '../../contexts/AuthContext';

export default function SystemSettingsPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [queueStats, setQueueStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Load settings
  useEffect(() => {
    loadSettings();
    const interval = setInterval(loadQueueStats, 5000); // Refresh queue stats every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const loadSettings = async () => {
    try {
      const data = await SystemSettingsService.getSettings();
      setSettings(data);
      setLoading(false);
    } catch (err) {
      setError('Error loading settings: ' + err.message);
      setLoading(false);
    }
  };

  const loadQueueStats = async () => {
    try {
      const stats = await SystemSettingsService.getQueueStats();
      setQueueStats(stats);
    } catch (err) {
      console.error('Error loading queue stats:', err);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      await SystemSettingsService.updateSettings(settings, user.uid);
      setSuccess(true);
    } catch (err) {
      setError('Error saving settings: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        System Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Settings saved successfully!
        </Alert>
      )}

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Registration Settings
          </Typography>
          
          <Box mt={2} mb={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings?.disableRegularSignUp || false}
                  onChange={(e) => handleChange('disableRegularSignUp', e.target.checked)}
                />
              }
              label="Referral-Only Mode"
            />
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              When enabled, only users with valid affiliate/referral links can register.
              Regular sign-up will be disabled. For beta testing with single-use codes, use the Beta Management tab instead.
            </Typography>
          </Box>
        </CardContent>
      </Card>
      
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Task Queue Settings
          </Typography>

          <Box display="grid" gap={3} gridTemplateColumns="repeat(auto-fill, minmax(250px, 1fr))">
            <TextField
              label="Max Concurrent Tasks"
              type="number"
              value={settings.maxConcurrentTasks}
              onChange={(e) => handleChange('maxConcurrentTasks', parseInt(e.target.value))}
              fullWidth
            />

            <TextField
              label="Max Queue Size"
              type="number"
              value={settings.maxQueueSize}
              onChange={(e) => handleChange('maxQueueSize', parseInt(e.target.value))}
              fullWidth
            />

            <TextField
              label="Task Timeout (minutes)"
              type="number"
              value={settings.taskTimeoutMinutes}
              onChange={(e) => handleChange('taskTimeoutMinutes', parseInt(e.target.value))}
              fullWidth
            />

            <TextField
              label="Retry Attempts"
              type="number"
              value={settings.retryAttempts}
              onChange={(e) => handleChange('retryAttempts', parseInt(e.target.value))}
              fullWidth
            />

            <TextField
              label="Retry Delay (seconds)"
              type="number"
              value={settings.retryDelaySeconds}
              onChange={(e) => handleChange('retryDelaySeconds', parseInt(e.target.value))}
              fullWidth
            />
          </Box>

          <Box mt={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.taskQueueEnabled}
                  onChange={(e) => handleChange('taskQueueEnabled', e.target.checked)}
                />
              }
              label="Enable Task Queue"
            />
          </Box>
        </CardContent>
      </Card>

      {queueStats && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Current Queue Status
            </Typography>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Running</TableCell>
                    <TableCell align="right">{queueStats.running}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Queued</TableCell>
                    <TableCell align="right">{queueStats.queued}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Completed (24h)</TableCell>
                    <TableCell align="right">{queueStats.completed24h}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Failed (24h)</TableCell>
                    <TableCell align="right">{queueStats.failed24h}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? <CircularProgress size={24} /> : 'Save Settings'}
        </Button>
      </Box>
    </Box>
  );
}
